import React, {
  useState,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useTheme } from "../../context/ThemeContext";
import { getTheme } from "../../theme";
import MainLayout from "../../components/Layout/MainLayout";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import { api } from "../../services/api";
import {
  Add,
  Edit,
  Delete,
  Code,
  CheckCircle,
  Cancel,
  TrendingUp,
  AttachMoney,
  Storage,
  People,
  Refresh,
  Download,
} from "@mui/icons-material";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xl};
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin: 0 0
    ${(props) => getTheme(props.theme?.isDark).spacing.md} 0;
  padding-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  border-bottom: 2px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
`;

const SectionCard = styled(Card)`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
  height: fit-content;
`;

// Prompt Templates Styles
const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const Filters = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  align-items: center;
`;

const Select = styled.select`
  padding: ${(props) =>
      getTheme(props.theme?.isDark).spacing.sm}
    ${(props) => getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  cursor: pointer;
`;

const TemplateGroup = styled.div`
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const GroupHeader = styled.div`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.hover};
  border-bottom: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: ${(props) =>
      getTheme(props.theme?.isDark).borderRadius.md}
    ${(props) =>
      getTheme(props.theme?.isDark).borderRadius.md}
    0 0;
`;

const GroupTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const TemplatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(300px, 1fr)
  );
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-top: none;
  border-radius: 0 0
    ${(props) =>
      getTheme(props.theme?.isDark).borderRadius.md}
    ${(props) =>
      getTheme(props.theme?.isDark).borderRadius.md};
`;

const TemplateCard = styled.div`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const LocaleBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${(props) =>
    props.active ? "#4CAF50" : "#757575"};
  color: white;
  margin-right: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const TemplateInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const ActionButton = styled.button`
  padding: 6px;
  background: transparent;
  border: none;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  cursor: pointer;
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.hover};
    color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
  }
`;

// Usage Statistics Styles
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(250px, 1fr)
  );
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const StatCard = styled.div`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary};
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
`;

const ChartTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin: 0 0
    ${(props) => getTheme(props.theme?.isDark).spacing.md} 0;
`;

// Prompt Usage Styles
const FiltersContainer = styled.div`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
`;

const FiltersRow = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  flex-wrap: wrap;
  align-items: center;
`;

const Input = styled.input`
  padding: ${(props) =>
      getTheme(props.theme?.isDark).spacing.sm}
    ${(props) => getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  min-width: 150px;
`;

const ActionButtonStyled = styled.button`
  padding: ${(props) =>
      getTheme(props.theme?.isDark).spacing.sm}
    ${(props) => getTheme(props.theme?.isDark).spacing.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary};
  color: white;
  border: none;
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
`;

const TableHeader = styled.thead`
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.hover};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};

  &:hover {
    background-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.hover};
  }
`;

const TableCell = styled.td`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  text-align: left;
  font-size: 14px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
`;

const TableHeaderCell = styled.th`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const PaginationButton = styled.button`
  padding: ${(props) =>
      getTheme(props.theme?.isDark).spacing.sm}
    ${(props) => getTheme(props.theme?.isDark).spacing.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  cursor: pointer;
  font-size: 14px;

  &:hover:not(:disabled) {
    background-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.hover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PROMPT_KEYS = {
  ASSISTANT_INSTRUCTIONS: "assistant_instructions",
  MEAL_PLAN_GENERATION: "meal_plan_generation",
  MOTIVATION_MESSAGE: "motivation_message",
  NOTIFICATION_AI_SYSTEM: "notification_ai_system",
  MEAL_IMAGE_GENERATION: "meal_image_generation",
};

const PROMPT_KEY_LABELS = {
  [PROMPT_KEYS.ASSISTANT_INSTRUCTIONS]:
    "Assistant Instructions",
  [PROMPT_KEYS.MEAL_PLAN_GENERATION]:
    "Meal Plan Generation",
  [PROMPT_KEYS.MOTIVATION_MESSAGE]: "Motivation Message",
  [PROMPT_KEYS.NOTIFICATION_AI_SYSTEM]:
    "Notification AI System",
  [PROMPT_KEYS.MEAL_IMAGE_GENERATION]:
    "Meal Image Generation",
};

const LOCALE_LABELS = {
  az: "Azərbaycan",
  en: "English",
  ru: "Русский",
};

const PromptManagement = () => {
  const { isDark } = useTheme();
  const theme = { isDark };
  const navigate = useNavigate();

  // Prompt Templates State
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] =
    useState(true);
  const [filterKey, setFilterKey] = useState("");
  const [filterLocale, setFilterLocale] = useState("");

  // Usage Statistics State
  const [stats, setStats] = useState({
    totalUsages: 0,
    totalTokens: 0,
    totalCost: 0,
    uniqueCustomerCount: 0,
  });
  const [dailyStats, setDailyStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [days, setDays] = useState(30);

  // Prompt Usage State
  const [usages, setUsages] = useState([]);
  const [usagesLoading, setUsagesLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [filters, setFilters] = useState({
    promptKey: "",
    aiService: "",
    customer: "",
    startDate: "",
    endDate: "",
  });

  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const filter = {};
      if (filterKey) filter.key = filterKey;
      if (filterLocale) filter.locale = filterLocale;

      const response = await api.getPromptTemplates({
        filter,
        limit: 1000,
      });
      setTemplates(response.results || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setTemplatesLoading(false);
    }
  }, [filterKey, filterLocale]);

  const fetchStats = async () => {
    try {
      const response = await api.getPromptUsageStats();
      setStats(response);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchDailyStats = useCallback(async () => {
    try {
      const response = await api.getDailyUsageStats({
        days,
      });
      setDailyStats(response);
    } catch (error) {
      console.error("Error fetching daily stats:", error);
    }
  }, [days]);

  const fetchUsages = useCallback(async () => {
    setUsagesLoading(true);
    try {
      const filter = {};
      if (filters.promptKey)
        filter.promptKey = filters.promptKey;
      if (filters.aiService)
        filter.aiService = filters.aiService;
      if (filters.customer)
        filter.customer = filters.customer;
      if (filters.startDate)
        filter.startDate = filters.startDate;
      if (filters.endDate) filter.endDate = filters.endDate;

      const response = await api.getPromptUsages({
        filter,
        page,
        limit: 50,
        sortBy: "createdAt:desc",
      });

      setUsages(response.results || []);
      setTotalPages(response.totalPages || 1);
      setTotalResults(response.totalResults || 0);
    } catch (error) {
      console.error("Error fetching prompt usages:", error);
    } finally {
      setUsagesLoading(false);
    }
  }, [page, filters]);

  // Fetch Templates
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Fetch Stats
  useEffect(() => {
    fetchStats();
    fetchDailyStats();
    const interval = setInterval(() => {
      fetchStats();
      fetchDailyStats();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchDailyStats]);

  // Fetch Usages
  useEffect(() => {
    fetchUsages();
    const interval = setInterval(() => {
      fetchUsages();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchUsages]);

  const groupedTemplates = templates.reduce(
    (acc, template) => {
      const key = template.key || "unknown";
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(template);
      return acc;
    },
    {}
  );

  const handleEdit = (id) => {
    navigate(`/admin/prompt-templates/${id}`);
  };

  const handleDelete = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this template?"
      )
    ) {
      try {
        await api.deletePromptTemplate(id);
        fetchTemplates();
      } catch (error) {
        console.error("Error deleting template:", error);
        alert("Error deleting template");
      }
    }
  };

  const handleCreate = () => {
    navigate("/admin/prompt-templates/new");
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleExport = () => {
    const headers = [
      "Date",
      "Customer",
      "Prompt Key",
      "AI Service",
      "Model",
      "Prompt Tokens",
      "Completion Tokens",
      "Total Tokens",
      "Cost (USD)",
    ];
    const rows = usages.map((usage) => [
      new Date(usage.createdAt).toLocaleString(),
      usage.customer?.email || usage.customer?.id || "N/A",
      PROMPT_KEY_LABELS[usage.promptKey] || usage.promptKey,
      usage.aiService,
      usage.aiModel,
      usage.promptTokens,
      usage.completionTokens,
      usage.totalTokens,
      usage.cost.toFixed(6),
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `prompt-usages-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
  };

  const formatCurrency = (value) => {
    return `$${value.toFixed(6)}`;
  };

  const formatCurrencyShort = (value) => {
    return `$${value.toFixed(2)}`;
  };

  const formatNumber = (value) => {
    return value.toLocaleString();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  return (
    <MainLayout>
      <PageContainer theme={theme}>
        {/* Usage Statistics Section */}
        <SectionCard theme={theme}>
          <SectionTitle theme={theme}>
            Usage Statistics
          </SectionTitle>

          <StatsGrid theme={theme}>
            <StatCard theme={theme}>
              <StatHeader theme={theme}>
                <StatIcon theme={theme}>
                  <TrendingUp />
                </StatIcon>
                <div>
                  <StatLabel theme={theme}>
                    Total Usages
                  </StatLabel>
                  <StatValue theme={theme}>
                    {statsLoading
                      ? "..."
                      : formatNumber(stats.totalUsages)}
                  </StatValue>
                </div>
              </StatHeader>
            </StatCard>

            <StatCard theme={theme}>
              <StatHeader theme={theme}>
                <StatIcon theme={theme}>
                  <Storage />
                </StatIcon>
                <div>
                  <StatLabel theme={theme}>
                    Total Tokens
                  </StatLabel>
                  <StatValue theme={theme}>
                    {statsLoading
                      ? "..."
                      : formatNumber(stats.totalTokens)}
                  </StatValue>
                </div>
              </StatHeader>
            </StatCard>

            <StatCard theme={theme}>
              <StatHeader theme={theme}>
                <StatIcon theme={theme}>
                  <AttachMoney />
                </StatIcon>
                <div>
                  <StatLabel theme={theme}>
                    Total Cost
                  </StatLabel>
                  <StatValue theme={theme}>
                    {statsLoading
                      ? "..."
                      : formatCurrencyShort(
                          stats.totalCost
                        )}
                  </StatValue>
                </div>
              </StatHeader>
            </StatCard>

            <StatCard theme={theme}>
              <StatHeader theme={theme}>
                <StatIcon theme={theme}>
                  <People />
                </StatIcon>
                <div>
                  <StatLabel theme={theme}>
                    Unique Customers
                  </StatLabel>
                  <StatValue theme={theme}>
                    {statsLoading
                      ? "..."
                      : formatNumber(
                          stats.uniqueCustomerCount
                        )}
                  </StatValue>
                </div>
              </StatHeader>
            </StatCard>
          </StatsGrid>

          <div>
            <ChartTitle theme={theme}>
              Daily Usage (Last {days} days)
            </ChartTitle>
            <Select
              theme={theme}
              value={days}
              onChange={(e) =>
                setDays(parseInt(e.target.value, 10))
              }
              style={{
                marginBottom: getTheme(theme.isDark).spacing
                  .md,
              }}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </Select>
            {dailyStats.length === 0 ? (
              <div
                style={{
                  padding: "32px",
                  textAlign: "center",
                }}
              >
                No data available
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        borderBottom: `1px solid ${
                          getTheme(theme.isDark).colors
                            .border
                        }`,
                      }}
                    >
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                        }}
                      >
                        Date
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "right",
                        }}
                      >
                        Usages
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "right",
                        }}
                      >
                        Tokens
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "right",
                        }}
                      >
                        Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyStats.map((stat, index) => (
                      <tr
                        key={index}
                        style={{
                          borderBottom: `1px solid ${
                            getTheme(theme.isDark).colors
                              .border
                          }`,
                        }}
                      >
                        <td style={{ padding: "12px" }}>
                          {stat.date}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "right",
                          }}
                        >
                          {formatNumber(stat.totalUsages)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "right",
                          }}
                        >
                          {formatNumber(stat.totalTokens)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "right",
                          }}
                        >
                          {formatCurrencyShort(
                            stat.totalCost
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </SectionCard>

        {/* Two Column Layout: Templates and Usage */}
        <TwoColumnLayout theme={theme}>
          {/* Prompt Templates Section */}
          <SectionCard theme={theme}>
            <SectionTitle theme={theme}>
              Prompt Templates
            </SectionTitle>
            <PageHeader theme={theme}>
              <Filters theme={theme}>
                <Select
                  theme={theme}
                  value={filterKey}
                  onChange={(e) =>
                    setFilterKey(e.target.value)
                  }
                >
                  <option value="">All Keys</option>
                  {Object.entries(PROMPT_KEY_LABELS).map(
                    ([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    )
                  )}
                </Select>
                <Select
                  theme={theme}
                  value={filterLocale}
                  onChange={(e) =>
                    setFilterLocale(e.target.value)
                  }
                >
                  <option value="">All Locales</option>
                  {Object.entries(LOCALE_LABELS).map(
                    ([code, label]) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    )
                  )}
                </Select>
              </Filters>
              <Button
                theme={theme}
                onClick={handleCreate}
                startIcon={<Add />}
              >
                Create Template
              </Button>
            </PageHeader>

            {templatesLoading ? (
              <div
                style={{
                  padding: "32px",
                  textAlign: "center",
                }}
              >
                Loading templates...
              </div>
            ) : Object.keys(groupedTemplates).length ===
              0 ? (
              <div
                style={{
                  padding: "32px",
                  textAlign: "center",
                }}
              >
                <p>
                  No templates found. Create your first
                  template!
                </p>
                <Button
                  theme={theme}
                  onClick={handleCreate}
                  startIcon={<Add />}
                  style={{ marginTop: "16px" }}
                >
                  Create Template
                </Button>
              </div>
            ) : (
              Object.entries(groupedTemplates).map(
                ([key, templatesList]) => (
                  <TemplateGroup key={key} theme={theme}>
                    <GroupHeader theme={theme}>
                      <GroupTitle theme={theme}>
                        <Code />
                        {PROMPT_KEY_LABELS[key] || key}
                      </GroupTitle>
                    </GroupHeader>
                    <TemplatesGrid theme={theme}>
                      {templatesList.map((template) => (
                        <TemplateCard
                          key={template.id}
                          theme={theme}
                          onClick={() =>
                            handleEdit(template.id)
                          }
                        >
                          <div>
                            <LocaleBadge
                              active={template.isActive}
                            >
                              {LOCALE_LABELS[
                                template.locale
                              ] || template.locale}
                            </LocaleBadge>
                            {template.isActive ? (
                              <CheckCircle
                                style={{
                                  fontSize: 16,
                                  color: "#4CAF50",
                                  verticalAlign: "middle",
                                }}
                              />
                            ) : (
                              <Cancel
                                style={{
                                  fontSize: 16,
                                  color: "#757575",
                                  verticalAlign: "middle",
                                }}
                              />
                            )}
                          </div>
                          {template.description && (
                            <p
                              style={{
                                marginTop: "8px",
                                fontSize: "14px",
                                color: getTheme(
                                  theme.isDark
                                ).colors.textSecondary,
                              }}
                            >
                              {template.description}
                            </p>
                          )}
                          <TemplateInfo theme={theme}>
                            <span
                              style={{
                                fontSize: "12px",
                                color: getTheme(
                                  theme.isDark
                                ).colors.textSecondary,
                              }}
                            >
                              {template.content?.substring(
                                0,
                                100
                              )}
                              ...
                            </span>
                            <ActionButtons
                              onClick={(e) =>
                                e.stopPropagation()
                              }
                            >
                              <ActionButton
                                theme={theme}
                                onClick={() =>
                                  handleEdit(template.id)
                                }
                              >
                                <Edit fontSize="small" />
                              </ActionButton>
                              <ActionButton
                                theme={theme}
                                onClick={() =>
                                  handleDelete(template.id)
                                }
                              >
                                <Delete fontSize="small" />
                              </ActionButton>
                            </ActionButtons>
                          </TemplateInfo>
                        </TemplateCard>
                      ))}
                    </TemplatesGrid>
                  </TemplateGroup>
                )
              )
            )}
          </SectionCard>

          {/* Prompt Usage Section */}
          <SectionCard theme={theme}>
            <SectionTitle theme={theme}>
              Prompt Usage Tracking
            </SectionTitle>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "16px",
                marginBottom: getTheme(theme.isDark).spacing
                  .md,
              }}
            >
              <ActionButtonStyled
                theme={theme}
                onClick={handleExport}
              >
                <Download />
                Export CSV
              </ActionButtonStyled>
              <ActionButtonStyled
                theme={theme}
                onClick={fetchUsages}
                disabled={usagesLoading}
              >
                <Refresh />
                Refresh
              </ActionButtonStyled>
            </div>

            <FiltersContainer theme={theme}>
              <FiltersRow theme={theme}>
                <Select
                  theme={theme}
                  value={filters.promptKey}
                  onChange={(e) =>
                    handleFilterChange(
                      "promptKey",
                      e.target.value
                    )
                  }
                >
                  <option value="">All Prompt Keys</option>
                  {Object.entries(PROMPT_KEY_LABELS).map(
                    ([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    )
                  )}
                </Select>
                <Select
                  theme={theme}
                  value={filters.aiService}
                  onChange={(e) =>
                    handleFilterChange(
                      "aiService",
                      e.target.value
                    )
                  }
                >
                  <option value="">All AI Services</option>
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">
                    Anthropic
                  </option>
                  <option value="google">Google</option>
                </Select>
                <Input
                  theme={theme}
                  type="text"
                  placeholder="Customer ID"
                  value={filters.customer}
                  onChange={(e) =>
                    handleFilterChange(
                      "customer",
                      e.target.value
                    )
                  }
                />
                <Input
                  theme={theme}
                  type="date"
                  placeholder="Start Date"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange(
                      "startDate",
                      e.target.value
                    )
                  }
                />
                <Input
                  theme={theme}
                  type="date"
                  placeholder="End Date"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange(
                      "endDate",
                      e.target.value
                    )
                  }
                />
              </FiltersRow>
            </FiltersContainer>

            {usagesLoading ? (
              <div
                style={{
                  padding: "32px",
                  textAlign: "center",
                }}
              >
                Loading...
              </div>
            ) : usages.length === 0 ? (
              <div
                style={{
                  padding: "32px",
                  textAlign: "center",
                }}
              >
                No prompt usages found
              </div>
            ) : (
              <>
                <Table theme={theme}>
                  <TableHeader theme={theme}>
                    <TableRow theme={theme}>
                      <TableHeaderCell theme={theme}>
                        Date
                      </TableHeaderCell>
                      <TableHeaderCell theme={theme}>
                        Customer
                      </TableHeaderCell>
                      <TableHeaderCell theme={theme}>
                        Prompt Key
                      </TableHeaderCell>
                      <TableHeaderCell theme={theme}>
                        AI Service
                      </TableHeaderCell>
                      <TableHeaderCell theme={theme}>
                        Model
                      </TableHeaderCell>
                      <TableHeaderCell theme={theme}>
                        Prompt Tokens
                      </TableHeaderCell>
                      <TableHeaderCell theme={theme}>
                        Completion Tokens
                      </TableHeaderCell>
                      <TableHeaderCell theme={theme}>
                        Total Tokens
                      </TableHeaderCell>
                      <TableHeaderCell theme={theme}>
                        Cost
                      </TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <tbody>
                    {usages.map((usage) => (
                      <TableRow
                        key={usage.id}
                        theme={theme}
                      >
                        <TableCell theme={theme}>
                          {formatDate(usage.createdAt)}
                        </TableCell>
                        <TableCell theme={theme}>
                          {usage.customer?.email ||
                            usage.customer?.name ||
                            usage.customer?.id ||
                            "N/A"}
                        </TableCell>
                        <TableCell theme={theme}>
                          {PROMPT_KEY_LABELS[
                            usage.promptKey
                          ] || usage.promptKey}
                        </TableCell>
                        <TableCell theme={theme}>
                          {usage.aiService}
                        </TableCell>
                        <TableCell theme={theme}>
                          {usage.aiModel}
                        </TableCell>
                        <TableCell theme={theme}>
                          {usage.promptTokens.toLocaleString()}
                        </TableCell>
                        <TableCell theme={theme}>
                          {usage.completionTokens.toLocaleString()}
                        </TableCell>
                        <TableCell theme={theme}>
                          {usage.totalTokens.toLocaleString()}
                        </TableCell>
                        <TableCell theme={theme}>
                          {formatCurrency(usage.cost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
                <Pagination theme={theme}>
                  <div>
                    Showing {(page - 1) * 50 + 1} to{" "}
                    {Math.min(page * 50, totalResults)} of{" "}
                    {totalResults} results
                  </div>
                  <div
                    style={{ display: "flex", gap: "8px" }}
                  >
                    <PaginationButton
                      theme={theme}
                      onClick={() =>
                        setPage((p) => Math.max(1, p - 1))
                      }
                      disabled={page === 1}
                    >
                      Previous
                    </PaginationButton>
                    <PaginationButton
                      theme={theme}
                      onClick={() =>
                        setPage((p) =>
                          Math.min(totalPages, p + 1)
                        )
                      }
                      disabled={page === totalPages}
                    >
                      Next
                    </PaginationButton>
                  </div>
                </Pagination>
              </>
            )}
          </SectionCard>
        </TwoColumnLayout>
      </PageContainer>
    </MainLayout>
  );
};

export default PromptManagement;
